import {
  AgentOrderPagingResponse,
  AgentOrderPagingResponseBasePagingResponse,
  BookingPagingResponse,
  BookingPagingResponseBasePagingResponse,
} from '@/__generated';
import NtaDataGrid, { NTADataGridColumnProps } from '@/components/NtaDataGrid/NtaDataGrid';
import Spin from '@/components/Spin';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { apiSdk } from '@/httpClient';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { DEFAULT_PAGE_SIZE, UNKNOWN_ERROR_MESSAGE, appDisplayDateFormat } from '@/config';
import NtaTag from '@/components/NtaTag';
import { BookingStatus, OrderStatus } from '@/constants';
import { NTACurrencyFormat } from '@/hooks/useCurrencyFormat';
import SearchForm from './SearchForm';
import EmptyRowsRenderer from '@/components/NtaDataGrid/EmptyRowsRenderer';
import { AppUrls } from '@/urls';
import { useGetUseRole } from '@/hooks/useGetUserRole';
import { useHistory } from 'react-router-dom';
import PriceOrTBA from '@/components/App/BookingComponents/PriceOrTBA';

const columns: NTADataGridColumnProps<AgentOrderPagingResponse>[] = [
  {
    key: 'orderId',
    name: <Trans>agentBookingListPage_OrderId</Trans>,
    width: 160,
    frozen: true,
    renderCell: ({ row }) => (
      <a target="_blank" rel="noreferrer" className="text-blue-1" href={AppUrls.__createAgentOrderDetailUrl(row.orderId || '')}>
        <b>{row.orderId}</b>
      </a>
    ),
  },

  {
    key: 'orderStatus',
    name: <Trans>Order status</Trans>,
    width: 160,
    frozen: true,
    renderCell: ({ row }) => <NtaTag color={OrderStatus.__getColor(row.orderStatus)} text={OrderStatus.__getValue(row.orderStatus)} />,
  },

  {
    key: 'bookingDate',
    name: <Trans>Order date</Trans>,
    width: 180,
    renderCell: ({ row }) => appDisplayDateFormat.default.withTime(row.orderDate),
  },

  {
    key: 'checkinDate',
    name: <Trans>agentBookingListPage_CheckinDate</Trans>,
    width: 150,
    renderCell: ({ row }) => appDisplayDateFormat.default.dateOnly(row.checkinDate),
  },
  {
    key: 'checkoutDate',
    name: <Trans>agentBookingListPage_CheckoutDate</Trans>,
    width: 150,
    renderCell: ({ row }) => appDisplayDateFormat.default.dateOnly(row.checkoutDate),
  },
  {
    key: 'totalPrice',
    name: <Trans>Selling Price</Trans>,
    width: 150,
    renderCell: ({ row }) => (
      <PriceOrTBA
        cancelledAt={row.cancelledAt}
        currency={'JPY'}
        faCancelChargeDate={row.faCancelChargeDate}
        price={row.sellingPrice}
        source={row.source}
      />
    ),
  },
  {
    key: 'hotelName',
    name: <Trans>agentBookingListPage_HotelName</Trans>,
    minWidth: 200,
    renderCell: ({ row }) => (
      <span className="text-truncate" title={row.hotelName}>
        {row.hotelName}
      </span>
    ),
  },
  {
    key: 'leadPassengerName',
    name: <Trans>agentBookingListPage_LeadPassenger</Trans>,
    minWidth: 180,
    renderCell: ({ row }) => (
      <span className="text-truncate" title={row.leadPassengerName}>
        {row.leadPassengerName}
      </span>
    ),
  },

  {
    key: 'supplierRefNumbers',
    name: <Trans>agentBookingListPage_SupplierRefNumbers</Trans>,
    width: 180,
    renderCell: ({ row }) => (
      <span className="text-truncate" title={row.supplierRefNumbers?.join(',')}>
        {row.supplierRefNumbers?.join(',')}
      </span>
    ),
  },
];
interface SearchParams {
  bookingStatus?: any;
  paymentStatus?: Array<any>;
  /**  */
  pageIndex?: number;
  /**  */
  pageSize?: number;
  /**  */
  orderBy?: string;
  /**  */
  orderByAsc?: boolean;
}

export default function AgentOrderBookingsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [bookingResponse, setBookingResponse] = React.useState<AgentOrderPagingResponseBasePagingResponse>();
  const [searchParams, setSearchParams] = React.useState<SearchParams>({
    pageSize: DEFAULT_PAGE_SIZE,
    pageIndex: 1,
    orderBy: 'orderId',
    orderByAsc: false,
  });
  const { isAdmin, isBooking } = useGetUseRole();
  if (!isAdmin && !isBooking) {
    history.push(AppUrls.Page401);
    return null;
  }
  const getData = () => {
    setLoading(true);
    apiSdk.AgentOrderBookingService.getOrders({ body: searchParams })
      .then(res => {
        setBookingResponse(res);
      })
      .catch(() => {
        ModalUtils.error({ content: t(UNKNOWN_ERROR_MESSAGE) });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    getData();
  }, [searchParams]);

  const onSearch = values => {
    setSearchParams({
      pageSize: DEFAULT_PAGE_SIZE,
      pageIndex: 1,
      orderBy: 'orderId',
      orderByAsc: false,
      ...values,
      bookingStatus: values.bookingStatus?.length ? values.bookingStatus?.map(x => x.value) : undefined,
      paymentStatus: values.paymentStatus?.length ? values.paymentStatus?.map(x => x.value) : undefined,
    });
  };
  const onPageChange = (selectedItem: { selected: number }) => {
    setSearchParams(s => ({
      ...s,
      pageIndex: selectedItem.selected + 1,
    }));
  };
  return (
    <div>
      <div className="row y-gap-20 justify-between items-end pb-10 lg:pb-40 md:pb-32">
        <div className="col-12">
          <h1 className="text-30 lh-14 fw-600">{t('agentOrderBooking_Title')}</h1>
          <div className="text-15 text-light-1">{t('agentOrderBooking_PageSubTitle')}</div>
        </div>
      </div>

      <SearchForm onSearch={onSearch} />

      <div className="py-30 px-30 rounded-4 bg-white shadow-3">
        <Spin loading={loading}>
          <NtaDataGrid<BookingPagingResponse>
            key={'orderId'}
            columns={columns}
            rows={bookingResponse?.data || []}
            columnsKeyConfig="/agent-order-list-page"
            totalRecord={bookingResponse?.total}
            pageIndex={bookingResponse?.pageIndex}
            pageSize={bookingResponse?.pageSize}
            onPageChange={onPageChange}
            renderers={{ noRowsFallback: <EmptyRowsRenderer /> }}
          />
        </Spin>
      </div>
    </div>
  );
}
